import { useSSRContext as vueUseSSRContext } from 'vue';
import asyncHooks from 'node:async_hooks';
import { useAppId } from './plugins/app-id';
let storage;
if (import.meta.env.SSR) {
    storage = new asyncHooks.AsyncLocalStorage();
}
/**
 * On the server this function will wrap the provided function `fn` in an
 * AsyncLocalStorage context (`store`). This function is only intended to be
 * called server side, but if it is called client side the function will be
 * called without entering into the context since the context is provided by
 * other means.
 *
 * @internal
 */
export function run(store, fn, ...args) {
    if (import.meta.env.SSR) {
        if (!storage)
            throw new TypeError('Async local storage has not been created, is import.meta.env.SSR properly set');
        return storage.run(store, fn, ...args);
    }
    else {
        return fn(...args);
    }
}
/**
 * On the server this function will return the ssr context object which you can
 * modify to add additional context values.
 *
 * Vite/vue-server-renderer adds some information about modules which we use for
 * preloading, and also some information about potential
 * {@link https://vuejs.org/guide/built-ins/teleport.html Teleports}. Note that
 * zsc-plugin-vite-ssr currently does not handle Teleports on the server side,
 * and vue recommends that Teleports are created only on the client side.
 *
 * The context object is also used internally by zsc-plugin-vite-ssr to set
 * state that should be forwarded from the server to the client, which might
 * be useful for hydration purposes. **HOWEVER**, to set and access this state,
 * please use {@link addServerState} and {@link getServerState} since those
 * function avoid collisions with other fragments.
 *
 * On the client, this function will return undefined.
 */
export function useSSRContext() {
    return storage?.getStore() ?? vueUseSSRContext();
}
/**
 * This function only works server side, it adds data that will be sent to the
 * client and available there using {@link getServerState}, it's intended for
 * passing state server->client for hydration purposes.
 *
 * Calling this function on the client will throw an error, since these
 * functions are not intended to be used for state management, and does not
 * provide reactive data structures.
 *
 * For example, to hydrate {@link https://pinia.vuejs.org/ Pinia}, add the
 * following to your {@link defineGlobalHooks hooks}:
 *
 * ```ts
 * export default defineGlobalHooks({
 *   [...]
 *   onRenderCompleted ({ app }) {
 *     // Assuming you've installed and set up Pinia.
 *     const pinia = app.config.globalProperties.$pinia
 *     // This takes all the state you've modified or set on the server
 *     // and sends it to the client.
 *     addServerState('pinia', pinia.state.value)
 *   }
 * })
 * ```
 *
 * @param key The state name, passed to {@link getServerState} to recall the
 *            same value.
 * @param value The state for the given key
 * @typeParam T the shape of the data to be stored
 */
export function addServerState(key, value) {
    if (import.meta.env.SSR) {
        const ctx = useSSRContext();
        if (!ctx)
            return;
        ctx.serverState ??= {};
        ctx.serverState[`${__APP_NAME__}_${ctx.appId}_${key}`] = value;
    }
    else {
        throw new Error('addServerState can only be called on the server');
    }
}
/**
 * This function works both client side and server side, it retrieves data that
 * was set on the server using {@link addServerState}. It's intended for
 * hydration on the client of data passed server->client, but can be used on the
 * server as well as a convenient shortcut instead of working with the
 * {@link useSSRContext SSR Context} directly.
 *
 * For example, to set up {@link https://pinia.vuejs.org/ Pinia} and hydrate it
 * with the state generated on the server and set with {@link addServerState},
 * add the following to your {@link defineGlobalHooks hooks}:
 *
 * ```ts
 * export default defineGlobalHooks({
 *   setupVue ({ app }) {
 *     const pinia = createPinia()
 *     app.use(pinia)
 *
 *     // This hydrates pinia on the client
 *     pinia.state.value = getServerState('pinia', app.config.globalProperties.$appId) ?? {}
 *   },
 *   [...]
 * })
 * ```
 *
 * @param key the key originally passed to {@link addServerState}
 * @param appId if the function is ran outside of a setup() context you need to
 *              provide the currently mounted app's appId, if you have access
 *              to the vue app instance it's available on
 *              `app.config.globalProperties.$appId`, and if you're inside vue
 *              options API it's available as `this.$appId`.
 * @typeParam T Since you likely know the type of the data you set on the server
 *              you can provide that type here to get a cleaner return type.
 */
export function getServerState(key, appId) {
    if (import.meta.env.SSR) {
        const ctx = useSSRContext();
        return ctx?.serverState?.[`${__APP_NAME__}_${ctx.appId}_${key}`];
    }
    else {
        return window.__VITE_STATE__?.[`${__APP_NAME__}_${appId ?? useAppId()}_${key}`];
    }
}
/**
 * This function, called server side, will set a response header or change it
 * from its default. Passing null as the value for the header will delete the
 * header
 */
export function setResponseHeader(key, value) {
    if (import.meta.env.SSR) {
        const ctx = useSSRContext();
        if (!ctx)
            return;
        if (value != null)
            ctx.responseHeaders[key.toLocaleLowerCase()] = value;
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        else
            delete ctx.responseHeaders[key.toLocaleLowerCase()];
    }
    else {
        throw new Error('setResponseHeader can only be called on the server');
    }
}
/**
 * This function, called server side, will change the status code for the
 * response from 200.
 *
 * Note: Setting the status code to 404 or 5xx will not trigger the plugin's
 * normal 404/5xx handling but will simply send the page rendered by you with
 * a 404 status code. However, setting the next argument to true will fall back
 * to the plugin's default handling for that status code, if defined, or return
 * your response with the status code provided.
 */
export function setStatusCode(code, next = false) {
    if (import.meta.env.SSR) {
        const ctx = useSSRContext();
        if (!ctx)
            return;
        if (code < 100 || code > 999)
            throw new Error('Invalid status code, must be between 100 and 999');
        if (next)
            ctx.responseStatus.statusCode = code + 1000;
        else
            ctx.responseStatus.statusCode = code;
    }
    else {
        throw new Error('setStatusCode can only be called on the server');
    }
}
//# sourceMappingURL=state.js.map